'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Container,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { useLoginForm } from '@/infrastructure/auth';
import { EnvironmentBadge } from '@/shared/components/common';
import { SessionLimitModalContent } from './SessionLimitModalContent';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email address is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean(),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const {
    login,
    isLoading,
    error,
    sessionLimitState,
    closeSessionLimitModal,
  } = useLoginForm();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (formData: LoginFormData) => {
    await login({
      email: formData.email,
      password: formData.password,
      rememberMe: formData.rememberMe,
    });
  };

  return (
    <>
      <Container component="main" maxWidth="xs">
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            opacity: isLoading ? 0.6 : 1,
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: isLoading ? 'none' : 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: 160,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Image
                src="/images/logoPortrait.svg"
                alt="Logo"
                width={200}
                height={140}
                priority
                style={{ objectFit: 'contain' }}
              />
              <EnvironmentBadge
                sx={{ position: 'absolute', bottom: -20, right: -10 }}
              />
            </Box>
          </Box>

          <TextField
            {...register('email')}
            fullWidth
            id="email"
            label="Email address"
            type="email"
            autoComplete="email"
            autoFocus
            error={!!errors.email || !!error}
            helperText={errors.email?.message || ' '}
            disabled={isLoading}
          />

          <TextField
            {...register('password')}
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            error={!!errors.password || !!error}
            helperText={errors.password?.message || ' '}
            disabled={isLoading}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Box className="w-full flex items-center justify-between mt-2 mb-2">
            <FormControlLabel
              control={
                <Checkbox
                  {...register('rememberMe')}
                  id="rememberMe"
                  disabled={isLoading}
                  size="small"
                />
              }
              label="Remember me"
              slotProps={{
                typography: { variant: 'body2' },
              }}
            />
            <Link
              href="/forgot-password"
              className="text-primary hover:text-blue-800 text-sm transition-colors"
            >
              Forgot password
            </Link>
          </Box>

          <Collapse in={!!error} sx={{ width: '100%' }}>
            <div
              className="text-error text-center text-label-large"
              role="alert"
            >
              {error}
            </div>
          </Collapse>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              position: 'relative',
              height: 42,
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{
                    color: 'inherit',
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-10px',
                  }}
                />
                <Box component="span" sx={{ visibility: 'hidden' }}>
                  SIGN IN
                </Box>
              </>
            ) : (
              'SIGN IN'
            )}
          </Button>

          <Box className="w-full text-center mt-8 mb-2 text-sm text-gray-600">
            <span className="text-on-surface-variant">
              &copy; {new Date().getFullYear()} Platform Admin Module. All rights reserved
            </span>
            <a
              href="/legal/privacy-policy"
              className="mx-2 text-primary hover:text-blue-800"
            >
              Privacy Policy
            </a>
            <a
              href="/legal/terms-and-conditions"
              className="mx-2 text-primary hover:text-blue-800"
            >
              Terms and Conditions
            </a>
          </Box>
        </Box>
      </Container>

      {/* Session Limit Modal */}
      <Modal
        open={sessionLimitState !== null}
        onClose={closeSessionLimitModal}
        aria-labelledby="session-limit-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box>
          {sessionLimitState && (
            <SessionLimitModalContent
              state={sessionLimitState}
              onClose={closeSessionLimitModal}
            />
          )}
        </Box>
      </Modal>
    </>
  );
}
